import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import documentService from '../services/documentService';
import { useTheme } from '../context/ThemeContext';

const DocumentsTab = ({ projectId }) => {
  const { isDark } = useTheme();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadMetadata, setUploadMetadata] = useState({
    title: '',
    description: '',
    category: '',
    tags: '',
  });
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewingDocument, setViewingDocument] = useState(null);
  const [documentContent, setDocumentContent] = useState('');

  useEffect(() => {
    if (projectId) {
      loadDocuments();
    }
  }, [projectId]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const response = await documentService.listDocuments({ project_id: projectId });
      setDocuments(response.documents || []);
    } catch (err) {
      console.error('Error loading documents:', err);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      if (!uploadMetadata.title) {
        setUploadMetadata({ ...uploadMetadata, title: file.name });
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setLoading(true);
      const metadata = {
        ...uploadMetadata,
        project_id: projectId,
        tags: uploadMetadata.tags ? uploadMetadata.tags.split(',').map(t => t.trim()) : [],
      };
      await documentService.uploadDocument(selectedFile, metadata);
      setUploadOpen(false);
      setSelectedFile(null);
      setUploadMetadata({ title: '', description: '', category: '', tags: '' });
      loadDocuments();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload document');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      await documentService.downloadDocument(doc.id, doc.file_name);
    } catch (err) {
      setError('Failed to download document');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      setLoading(true);
      await documentService.deleteDocument(docId);
      loadDocuments();
    } catch (err) {
      setError('Failed to delete document');
    } finally {
      setLoading(false);
    }
  };

  const handleViewMarkdown = async (doc) => {
    try {
      setLoading(true);
      const fullDoc = await documentService.getDocument(doc.id);
      setViewingDocument(fullDoc);
      setDocumentContent(fullDoc.content || fullDoc.content_preview || '');
      setViewerOpen(true);
    } catch (err) {
      console.error('Error loading document content:', err);
      setError('Failed to load document content');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentClick = (doc) => {
    if (doc.document_type === 'MARKDOWN' || doc.document_type === 'TXT') {
      handleViewMarkdown(doc);
    }
  };

  const getIcon = (type) => {
    const icons = {
      MARKDOWN: '📝',
      PDF: '📄',
      DOCX: '📃',
      TXT: '📄',
      IMAGE: '🖼️',
      VIDEO: '🎥',
      SPREADSHEET: '📊',
      PRESENTATION: '📊',
      OTHER: '📎',
    };
    return icons[type] || '📎';
  };

  const formatSize = (bytes) => {
    if (!bytes) return 'N/A';
    const kb = bytes / 1024;
    return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`;
  };

  const formatDate = (date) => {
    return date ? new Date(date).toLocaleDateString() : 'N/A';
  };

  if (loading && !documents.length) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading documents...</div>;
  }

  return (
    <div>
      {error && (
        <div style={{
          padding: '0.75rem 1rem',
          backgroundColor: '#FEE2E2',
          border: '1px solid #FCA5A5',
          borderRadius: '0.375rem',
          marginBottom: '1rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ color: '#991B1B' }}>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.25rem' }}
          >
            ×
          </button>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h3 style={{ margin: 0, color: 'var(--gray-900)' }}>Documents ({documents.length})</h3>
        <button
          onClick={() => setUploadOpen(!uploadOpen)}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: 'var(--primary-600)',
            color: 'white',
            border: 'none',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          ☁️ Upload Document
        </button>
      </div>

      {uploadOpen && (
        <div style={{
          border: '1px solid var(--gray-300)',
          borderRadius: '0.5rem',
          padding: '1.5rem',
          marginBottom: '1.5rem',
          backgroundColor: 'var(--gray-50)'
        }}>
          <h4 style={{ marginTop: 0, marginBottom: '1rem' }}>Upload New Document</h4>
          
          <div style={{ marginBottom: '1rem' }}>
            <label
              htmlFor="file-upload"
              style={{
                display: 'inline-block',
                padding: '0.75rem 1.5rem',
                backgroundColor: 'white',
                border: '2px dashed var(--gray-300)',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                textAlign: 'center',
                width: '100%'
              }}
            >
              {selectedFile ? `📎 ${selectedFile.name}` : '☁️ Click to select file'}
              <input
                id="file-upload"
                type="file"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </label>
          </div>

          <input
            type="text"
            placeholder="Title"
            value={uploadMetadata.title}
            onChange={(e) => setUploadMetadata({ ...uploadMetadata, title: e.target.value })}
            style={{
              width: '100%',
              padding: '0.5rem',
              marginBottom: '0.75rem',
              border: '1px solid var(--gray-300)',
              borderRadius: '0.375rem'
            }}
          />

          <textarea
            placeholder="Description (optional)"
            value={uploadMetadata.description}
            onChange={(e) => setUploadMetadata({ ...uploadMetadata, description: e.target.value })}
            rows="2"
            style={{
              width: '100%',
              padding: '0.5rem',
              marginBottom: '0.75rem',
              border: '1px solid var(--gray-300)',
              borderRadius: '0.375rem',
              resize: 'vertical'
            }}
          />

          <input
            type="text"
            placeholder="Category (e.g., strategy, research)"
            value={uploadMetadata.category}
            onChange={(e) => setUploadMetadata({ ...uploadMetadata, category: e.target.value })}
            style={{
              width: '100%',
              padding: '0.5rem',
              marginBottom: '0.75rem',
              border: '1px solid var(--gray-300)',
              borderRadius: '0.375rem'
            }}
          />

          <input
            type="text"
            placeholder="Tags (comma-separated)"
            value={uploadMetadata.tags}
            onChange={(e) => setUploadMetadata({ ...uploadMetadata, tags: e.target.value })}
            style={{
              width: '100%',
              padding: '0.5rem',
              marginBottom: '1rem',
              border: '1px solid var(--gray-300)',
              borderRadius: '0.375rem'
            }}
          />

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={handleUpload}
              disabled={!selectedFile || loading}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: selectedFile && !loading ? 'var(--primary-600)' : 'var(--gray-400)',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: selectedFile && !loading ? 'pointer' : 'not-allowed',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              {loading ? 'Uploading...' : 'Upload'}
            </button>
            <button
              onClick={() => {
                setUploadOpen(false);
                setSelectedFile(null);
                setUploadMetadata({ title: '', description: '', category: '', tags: '' });
              }}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: 'white',
                color: 'var(--gray-700)',
                border: '1px solid var(--gray-300)',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {documents.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          backgroundColor: 'var(--gray-50)',
          border: '1px solid var(--gray-200)',
          borderRadius: '0.5rem'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📄</div>
          <p style={{ color: 'var(--gray-600)', margin: 0 }}>No documents yet</p>
          <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem', marginTop: '0.5rem' }}>
            Upload files or AI-generated content will appear here
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
          {documents.map((doc) => {
            const isClickable = doc.document_type === 'MARKDOWN' || doc.document_type === 'TXT';
            return (
            <div
              key={doc.id}
              onClick={() => isClickable && handleDocumentClick(doc)}
              style={{
                border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}`,
                borderRadius: '0.5rem',
                padding: '1rem',
                backgroundColor: isDark ? '#1f2937' : 'white',
                opacity: doc.is_archived ? 0.6 : 1,
                cursor: isClickable ? 'pointer' : 'default',
                transition: 'all 0.2s',
                ...(isClickable && { ':hover': { borderColor: '#6366f1' } })
              }}
              onMouseEnter={(e) => {
                if (isClickable) {
                  e.currentTarget.style.borderColor = '#6366f1';
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (isClickable) {
                  e.currentTarget.style.borderColor = isDark ? '#374151' : 'var(--gray-200)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                <div style={{ fontSize: '1.5rem', marginRight: '0.75rem' }}>
                  {getIcon(doc.document_type)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: '600',
                      color: isDark ? '#f9fafb' : 'var(--gray-900)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={doc.title}
                  >
                    {doc.title}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>
                    {formatSize(doc.file_size)} • {formatDate(doc.created_at)}
                  </div>
                </div>
              </div>

              {doc.description && (
                <p style={{
                  fontSize: '0.875rem',
                  color: isDark ? '#9ca3af' : 'var(--gray-600)',
                  marginBottom: '0.75rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical'
                }}>
                  {doc.description}
                </p>
              )}

              {doc.source && (
                <div style={{ marginBottom: '0.75rem' }}>
                  <span
                    style={{
                      fontSize: '0.75rem',
                      padding: '0.25rem 0.5rem',
                      backgroundColor: doc.source === 'AI_GENERATED' || doc.source === 'AI_ACTION' 
                        ? (isDark ? '#1e3a5f' : '#DBEAFE') 
                        : (isDark ? '#374151' : '#F3F4F6'),
                      color: doc.source === 'AI_GENERATED' || doc.source === 'AI_ACTION' 
                        ? (isDark ? '#93c5fd' : '#1E40AF') 
                        : (isDark ? '#e5e7eb' : '#374151'),
                      borderRadius: '0.25rem',
                      fontWeight: '500'
                    }}
                  >
                    {doc.source.replace('_', ' ')}
                  </span>
                </div>
              )}

              {doc.tags && doc.tags.length > 0 && (
                <div style={{ marginBottom: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                  {doc.tags.slice(0, 3).map((tag, idx) => (
                    <span
                      key={idx}
                      style={{
                        fontSize: '0.75rem',
                        padding: '0.125rem 0.5rem',
                        backgroundColor: isDark ? '#374151' : '#F3F4F6',
                        color: isDark ? '#e5e7eb' : '#4B5563',
                        borderRadius: '0.25rem'
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                  {doc.tags.length > 3 && (
                    <span
                      style={{
                        fontSize: '0.75rem',
                        padding: '0.125rem 0.5rem',
                        backgroundColor: isDark ? '#374151' : '#F3F4F6',
                        color: isDark ? '#e5e7eb' : '#4B5563',
                        borderRadius: '0.25rem'
                      }}
                    >
                      +{doc.tags.length - 3}
                    </span>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {isClickable && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDocumentClick(doc); }}
                    style={{
                      flex: 1,
                      padding: '0.375rem',
                      backgroundColor: isDark ? '#374151' : '#f0f9ff',
                      color: '#6366f1',
                      border: '1px solid #6366f1',
                      borderRadius: '0.375rem',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontWeight: '500'
                    }}
                    title="View"
                  >
                    👁️ View
                  </button>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}
                  style={{
                    flex: 1,
                    padding: '0.375rem',
                    backgroundColor: isDark ? '#1f2937' : 'white',
                    color: 'var(--primary-600)',
                    border: '1px solid var(--primary-600)',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: '500'
                  }}
                  title="Download"
                >
                  ⬇️ Download
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(doc.id); }}
                  style={{
                    padding: '0.375rem 0.75rem',
                    backgroundColor: isDark ? '#1f2937' : 'white',
                    color: '#DC2626',
                    border: '1px solid #DC2626',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    fontSize: '0.75rem'
                  }}
                  title="Delete"
                >
                  🗑️
                </button>
              </div>
            </div>
          );
          })}
        </div>
      )}

      {/* Markdown Viewer Modal */}
      {viewerOpen && viewingDocument && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '2rem'
          }}
          onClick={() => setViewerOpen(false)}
        >
          <div
            style={{
              backgroundColor: isDark ? '#1f2937' : 'white',
              borderRadius: '0.75rem',
              width: '100%',
              maxWidth: '900px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: isDark ? '1px solid #374151' : 'none'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '1rem 1.5rem',
                borderBottom: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ fontSize: '1.5rem' }}>📝</span>
                <div>
                  <h3 style={{ margin: 0, color: isDark ? '#f9fafb' : '#1f2937', fontSize: '1.125rem' }}>
                    {viewingDocument.title}
                  </h3>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                    {formatSize(viewingDocument.file_size)} • {formatDate(viewingDocument.created_at)}
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => handleDownload(viewingDocument)}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: isDark ? '#374151' : '#f3f4f6',
                    color: isDark ? '#e5e7eb' : '#374151',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem'
                  }}
                >
                  ⬇️ Download
                </button>
                <button
                  onClick={() => setViewerOpen(false)}
                  style={{
                    padding: '0.5rem',
                    backgroundColor: 'transparent',
                    color: isDark ? '#9ca3af' : '#6b7280',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    fontSize: '1.5rem',
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ×
                </button>
              </div>
            </div>
            
            {/* Modal Body - Markdown Content */}
            <div
              style={{
                padding: '1.5rem',
                overflow: 'auto',
                flex: 1
              }}
              className={isDark ? 'markdown-dark' : 'markdown-light'}
            >
              <div
                style={{
                  color: isDark ? '#e5e7eb' : '#374151',
                  lineHeight: 1.7,
                  fontSize: '0.9375rem'
                }}
              >
                <ReactMarkdown
                  components={{
                    h1: ({ node, ...props }) => (
                      <h1 style={{ color: isDark ? '#f9fafb' : '#1f2937', marginTop: '1.5rem', marginBottom: '1rem', fontSize: '1.75rem', fontWeight: 700, borderBottom: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`, paddingBottom: '0.5rem' }} {...props} />
                    ),
                    h2: ({ node, ...props }) => (
                      <h2 style={{ color: isDark ? '#f9fafb' : '#1f2937', marginTop: '1.25rem', marginBottom: '0.75rem', fontSize: '1.5rem', fontWeight: 600 }} {...props} />
                    ),
                    h3: ({ node, ...props }) => (
                      <h3 style={{ color: isDark ? '#f9fafb' : '#1f2937', marginTop: '1rem', marginBottom: '0.5rem', fontSize: '1.25rem', fontWeight: 600 }} {...props} />
                    ),
                    p: ({ node, ...props }) => (
                      <p style={{ marginBottom: '1rem' }} {...props} />
                    ),
                    ul: ({ node, ...props }) => (
                      <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }} {...props} />
                    ),
                    ol: ({ node, ...props }) => (
                      <ol style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }} {...props} />
                    ),
                    li: ({ node, ...props }) => (
                      <li style={{ marginBottom: '0.25rem' }} {...props} />
                    ),
                    code: ({ node, inline, ...props }) => (
                      inline ? (
                        <code style={{ backgroundColor: isDark ? '#374151' : '#f3f4f6', padding: '0.125rem 0.375rem', borderRadius: '0.25rem', fontSize: '0.875em', color: isDark ? '#f472b6' : '#db2777' }} {...props} />
                      ) : (
                        <code style={{ display: 'block', backgroundColor: isDark ? '#374151' : '#f3f4f6', padding: '1rem', borderRadius: '0.5rem', fontSize: '0.875em', overflow: 'auto', marginBottom: '1rem' }} {...props} />
                      )
                    ),
                    pre: ({ node, ...props }) => (
                      <pre style={{ backgroundColor: isDark ? '#374151' : '#f3f4f6', padding: '1rem', borderRadius: '0.5rem', overflow: 'auto', marginBottom: '1rem' }} {...props} />
                    ),
                    blockquote: ({ node, ...props }) => (
                      <blockquote style={{ borderLeft: `4px solid ${isDark ? '#6366f1' : '#6366f1'}`, paddingLeft: '1rem', marginLeft: 0, marginBottom: '1rem', color: isDark ? '#9ca3af' : '#6b7280', fontStyle: 'italic' }} {...props} />
                    ),
                    a: ({ node, ...props }) => (
                      <a style={{ color: '#6366f1', textDecoration: 'none' }} {...props} />
                    ),
                    table: ({ node, ...props }) => (
                      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1rem' }} {...props} />
                    ),
                    th: ({ node, ...props }) => (
                      <th style={{ border: `1px solid ${isDark ? '#4b5563' : '#e5e7eb'}`, padding: '0.5rem', backgroundColor: isDark ? '#374151' : '#f9fafb', textAlign: 'left', fontWeight: 600 }} {...props} />
                    ),
                    td: ({ node, ...props }) => (
                      <td style={{ border: `1px solid ${isDark ? '#4b5563' : '#e5e7eb'}`, padding: '0.5rem' }} {...props} />
                    ),
                    hr: ({ node, ...props }) => (
                      <hr style={{ border: 'none', borderTop: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`, marginTop: '1.5rem', marginBottom: '1.5rem' }} {...props} />
                    ),
                  }}
                >
                  {documentContent}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsTab;
